import { createAsyncThunk } from '@reduxjs/toolkit';
import {
  createPickupPointRequest,
  deletePickupPointRequest,
  getPickupPointsRequest,
  updatePickupPointRequest,
} from './pickupPointsApi';
import { extractApiError } from '../../utils/errors';

/**
 * The operator's pickup points. Not paginated: an operator has a handful.
 *
 * @returns {Promise<Array<object>>}
 */
export const fetchPickupPoints = createAsyncThunk(
  'pickupPoints/fetchPickupPoints',
  async (_, { rejectWithValue }) => {
    try {
      const { data } = await getPickupPointsRequest();
      return data.pickup_points || [];
    } catch (error) {
      console.error('Error fetching pickup points:', error);
      return rejectWithValue(extractApiError(error, 'No se pudieron obtener los puntos de retiro'));
    }
  }
);

/**
 * @param {object} payload - See `payloadFromForm`.
 * @returns {Promise<object>} The point as the server stored it.
 */
export const createPickupPoint = createAsyncThunk(
  'pickupPoints/createPickupPoint',
  async (payload, { rejectWithValue }) => {
    try {
      const { data } = await createPickupPointRequest(payload);
      return data.pickup_point;
    } catch (error) {
      console.error('Error creating pickup point:', error);
      return rejectWithValue(extractApiError(error, 'No se pudo crear el punto de retiro'));
    }
  }
);

/**
 * @param {{pickupPointId: number, payload: object}} params
 * @returns {Promise<object>} The point as the server left it.
 */
export const updatePickupPoint = createAsyncThunk(
  'pickupPoints/updatePickupPoint',
  async ({ pickupPointId, payload }, { rejectWithValue }) => {
    try {
      const { data } = await updatePickupPointRequest(pickupPointId, payload);
      return data.pickup_point;
    } catch (error) {
      console.error('Error updating pickup point:', error);
      return rejectWithValue(extractApiError(error, 'No se pudo guardar el punto de retiro'));
    }
  }
);

/**
 * A soft delete. The server refuses while shipments are waiting at the point,
 * and says how many: that sentence is what the dialog shows.
 *
 * @param {number} pickupPointId
 * @returns {Promise<number>} The id removed.
 */
export const deletePickupPoint = createAsyncThunk(
  'pickupPoints/deletePickupPoint',
  async (pickupPointId, { rejectWithValue }) => {
    try {
      await deletePickupPointRequest(pickupPointId);
      return pickupPointId;
    } catch (error) {
      console.error('Error deleting pickup point:', error);
      return rejectWithValue(extractApiError(error, 'No se pudo eliminar el punto de retiro'));
    }
  }
);
